<script setup lang="ts">
import { useEventIdeasApi } from '~/composables/api/use-event-ideas-api';
import { useMyInterests } from '~/stores/use-my-interests';

// Community event-ideas list. Rendered as the "Community ideas" section on the
// combined `/browse` surface alongside the native community grid and the "More
// to discover" (Almanac) section. Ideas are lightweight, interest-gauging
// placeholders — never confirmed, joinable events — so the section carries no
// filters, only pagination.
//
// The `pageParam` prop names the query key this section uses for its pagination
// so it never collides with the community list's `page` NOR the discover
// section's `dpage` on the shared page. Default `ipage`.
const props = withDefaults(
  defineProps<{
    pageParam?: string;
  }>(),
  {
    pageParam: 'ipage',
  },
);

const config = useRuntimeConfig();
const route = useRoute();

const currentPage = computed(() => {
  const p = Number(route.query[props.pageParam]);
  return p > 0 ? p : 1;
});

const { data, status, refresh } = useAsyncData(
  `event-ideas-${props.pageParam}`,
  () => {
    const params: { page?: number } = {};
    if (currentPage.value > 1) params.page = currentPage.value;
    return useEventIdeasApi().list(params);
  },
  {
    // Watch only this section's own pagination so unrelated (community /
    // discover) query changes on the shared page don't trigger a refetch here.
    watch: [currentPage],
    server: !config.public.mock,
  },
);

function setPage(page: number) {
  navigateTo({
    query: {
      ...route.query,
      [props.pageParam]: page > 1 ? page : undefined,
    },
  });
}

// Warm the shared interest store so the idea cards' interest toggles seed their
// current rung once auth settles. Client + authed only; the store dedupes across
// the browse page's community + discover + ideas surfaces.
onMounted(() => {
  useMyInterests().ensureUpcoming();
});
</script>

<template>
  <div>
    <!-- Loading State (idle = SSR placeholder when server fetch is skipped) -->
    <v-row v-if="status === 'pending' || status === 'idle'">
      <v-col v-for="n in 3" :key="n" cols="12" sm="6" md="4">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-alert
      v-else-if="status === 'error'"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      <div class="d-flex align-center justify-space-between">
        <span>Failed to load ideas. Please try again.</span>
        <v-btn variant="text" color="error" @click="refresh()"> Retry </v-btn>
      </div>
    </v-alert>

    <!-- Empty State — a subtle note, framing ideas as an early-days invitation
         rather than a failure to find events. -->
    <div
      v-else-if="!data?.data.length"
      class="text-medium-emphasis text-body-2"
    >
      No community ideas floated yet — check back soon.
    </div>

    <!-- Idea Grid -->
    <template v-else>
      <v-row>
        <v-col
          v-for="event in data.data"
          :key="event.id"
          cols="12"
          sm="6"
          md="4"
        >
          <IdeasIdeaCard :event="event" />
        </v-col>
      </v-row>

      <!-- Pagination -->
      <div v-if="data.meta.last_page > 1" class="d-flex justify-center mt-8">
        <v-pagination
          :model-value="data.meta.current_page"
          :length="data.meta.last_page"
          @update:model-value="setPage"
        />
      </div>
    </template>
  </div>
</template>
